import { useMasterApprenticesStatStore } from '@/features/teacher-student-stat/store';
import { useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  NormalizedColumn,
  RepublicTableRow,
  TotalsMap,
} from '../../../../models';

interface UseTableReturn {
  columns: NormalizedColumn[];
  rows: RepublicTableRow[];
  totals: TotalsMap;
  isLoading: boolean;
  groupLabel: string | null;
  mastersTotal: number;
  apprenticesTotal: number;
}

const CONVERTED_LABEL: Record<string, string> = {
  region_name: 'Hudud nomi',
  masters_count: 'Ustoz f/x lar soni',
  apprentices_count: 'Shogird f/x lar soni',
  contours_count: 'Konturlar soni',
};

const MONTH_LABEL_UZ: Record<string, string> = {
  JANUARY: 'Yanvar',
  FEBRUARY: 'Fevral',
  MARCH: 'Mart',
  APRIL: 'Aprel',
  MAY: 'May',
  JUNE: 'Iyun',
  JULY: 'Iyul',
  AUGUST: 'Avgust',
  SEPTEMBER: 'Sentabr',
  OCTOBER: 'Oktabr',
  NOVEMBER: 'Noyabr',
  DECEMBER: 'Dekabr',
};

export default function useTable(): UseTableReturn {
  const {
    masterApprenticesRepublicResponse,
    fetchMasterApprenticesRepublicRates,
    isMasterApprenticesRepublicLoading,
  } = useMasterApprenticesStatStore();

  const [searchParams] = useSearchParams();
  const month = searchParams.get('month');

  useEffect(() => {
    fetchMasterApprenticesRepublicRates({ month: month! });
  }, [month]);

  const groupLabel = useMemo<string | null>(() => {
    const raw =
      masterApprenticesRepublicResponse?.columns?.dynamic?.group_value;
    if (!raw) return null;
    return MONTH_LABEL_UZ[raw] ?? raw;
  }, [masterApprenticesRepublicResponse]);

  const columns = useMemo<NormalizedColumn[]>(() => {
    if (!masterApprenticesRepublicResponse) return [];

    const { static: staticCols, dynamic } =
      masterApprenticesRepublicResponse.columns;

    const indexColumn: NormalizedColumn = {
      key: '__index',
      label: '№',
      aggregate: null,
      isDynamic: false,
    };

    const staticColumns: NormalizedColumn[] = staticCols.map((col) => ({
      key: col.key,
      label: CONVERTED_LABEL[col.key] ?? col.ui_label,
      aggregate: col.aggregate,
      isDynamic: false,
    }));

    const dynamicColumns: NormalizedColumn[] = dynamic.actions.map(
      (action) => ({
        key: `action_${action.action_id}`,
        label: action.action_name,
        aggregate: action.aggregate,
        isDynamic: true,
        actionId: action.action_id,
      }),
    );

    return [indexColumn, ...staticColumns, ...dynamicColumns];
  }, [masterApprenticesRepublicResponse]);

  const rows = useMemo<RepublicTableRow[]>(() => {
    return masterApprenticesRepublicResponse?.rows ?? [];
  }, [masterApprenticesRepublicResponse]);

  const totals = useMemo<TotalsMap>(() => {
    if (!rows.length || !columns.length) return {};

    const aggregatableStaticKeys = columns
      .filter((col) => !col.isDynamic && col.aggregate === 'total')
      .map((col) => col.key);

    return rows.reduce<TotalsMap>((acc, row) => {
      aggregatableStaticKeys.forEach((key) => {
        const value = row[key as keyof RepublicTableRow];
        acc[key] = (acc[key] ?? 0) + (typeof value === 'number' ? value : 0);
      });

      row.rated_actions.forEach((ra) => {
        const key = `action_${ra.action_id}`;
        acc[key] = (acc[key] ?? 0) + ra.rated_actions_count;
      });

      return acc;
    }, {});
  }, [rows, columns]);

  return {
    columns,
    rows,
    totals,
    isLoading: isMasterApprenticesRepublicLoading,
    groupLabel,
    mastersTotal: totals['masters_count'] ?? 0,
    apprenticesTotal: totals['apprentices_count'] ?? 0,
  };
}
